import type { TripDestination } from '@/services/trips';

/**
 * Normalize any date-like value to a YYYY-MM-DD string.
 */
export const toDateKey = (value: Date | string | undefined | null): string | null => {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  // IMPORTANT: use local calendar date components (NOT toISOString),
  // otherwise timezone offsets can shift the day and break multi-city date range matching.
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Find the destination that covers the given calendar day (inclusive, date-key based).
 */
export const getDestinationForDate = (
  destinations: TripDestination[] | undefined | null,
  date: Date | string,
): { destination: TripDestination; index: number } | null => {
  if (!destinations || destinations.length === 0) return null;
  const dayKey = toDateKey(date);
  if (!dayKey) return null;

  const matches = destinations
    .map((destination, index) => ({
      destination,
      index,
      startKey: toDateKey(destination.startDate),
      endKey: toDateKey(destination.endDate),
    }))
    .filter((m) => !!m.startKey && !!m.endKey && dayKey >= (m.startKey as string) && dayKey <= (m.endKey as string));

  if (matches.length === 0) return null;

  // If there are overlapping ranges (common on travel days), prefer the destination
  // that *starts* on this day; otherwise prefer the last matching destination.
  const startsToday = matches.filter((m) => m.startKey === dayKey);
  const best = (startsToday.length ? startsToday : matches).reduce((acc, cur) => (cur.index > acc.index ? cur : acc));

  return { destination: best.destination, index: best.index };
};

/**
 * Return all destinations that cover the given calendar day (inclusive, date-key based).
 * Useful for travel days where ranges may overlap.
 */
export const getDestinationsForDate = (
  destinations: TripDestination[] | undefined | null,
  date: Date | string,
): Array<{ destination: TripDestination; index: number }> => {
  if (!destinations || destinations.length === 0) return [];
  const dayKey = toDateKey(date);
  if (!dayKey) return [];

  return destinations
    .map((destination, index) => ({
      destination,
      index,
      startKey: toDateKey(destination.startDate),
      endKey: toDateKey(destination.endDate),
    }))
    .filter((m) => !!m.startKey && !!m.endKey && dayKey >= (m.startKey as string) && dayKey <= (m.endKey as string))
    .map((m) => ({ destination: m.destination, index: m.index }));
};

